import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Spin, Space } from 'antd';
import { Navigate } from 'react-router-dom';
import {
  UserOutlined,
  ToolOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  WarningOutlined,
  TrophyOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BajoStockCard from './BajoStockCard';
import VencimientosCard from '../components/VencimientosCard';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Redirigir operadores a Mis Servicios
  if (user?.rol === 'operador') {
    return <Navigate to="/mis-servicios" replace />;
  }

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard');
      if (response.data && typeof response.data === 'object') {
        setStats(response.data);
        // Debug: verificar mantenimientos próximos
        if (response.data.mantenimientos_proximos) {
          console.log('Mantenimientos próximos:', response.data.mantenimientos_proximos);
        }
      } else {
        console.error('Respuesta inválida del servidor:', response.data);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      if (error.response) {
        console.error('Detalles del error:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  const columns = [
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente',
      render: (text, record) => `${record.cliente_nombre} ${record.cliente_apellido}`,
    },
    {
      title: 'Vehículo',
      dataIndex: 'placa',
      key: 'vehiculo',
      render: (text, record) => record.placa || '-',
    },
    {
      title: 'Tipo de Servicio',
      dataIndex: 'tipo_servicio',
      key: 'tipo_servicio',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          pendiente: 'orange',
          en_proceso: 'blue',
          completado: 'green',
          cancelado: 'red',
        };
        return <Tag color={colors[estado]}>{estado === 'cancelado' ? 'ANULADO' : estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_solicitud',
      key: 'fecha',
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Dashboard</h1>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Clientes"
              value={stats?.total_clientes || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Servicios"
              value={stats?.total_servicios || 0}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Servicios Pendientes"
              value={stats?.servicios_pendientes || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ingresos del Mes"
              value={stats?.ingresos_mes || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Servicios Completados"
              value={stats?.servicios_completados || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Personal"
              value={stats?.total_personal || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>
      <Card
        title={
          <span>
            <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Mantenimientos Próximos (menos de 5 días o 100 km)
          </span>
        }
        style={{ marginTop: 24 }}
        headStyle={{ backgroundColor: '#fff7e6', borderColor: '#ffd591' }}
      >
        {stats?.mantenimientos_proximos && Array.isArray(stats.mantenimientos_proximos) && stats.mantenimientos_proximos.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <Table
              columns={[
                {
                  title: 'Placa',
                  dataIndex: 'placa',
                  key: 'placa',
                },
                {
                  title: 'Tipo de Grúa',
                  dataIndex: 'tipo_grua',
                  key: 'tipo_grua',
                },
                {
                  title: 'Próximo Mantenimiento',
                  key: 'mantenimiento',
                  render: (_, record) => {
                    const mantenimientos = [];

                    // Mantenimiento por fecha
                    if (record.proximo_mantenimiento) {
                      const diasRestantes = record.dias_restantes;
                      const fecha = dayjs(record.proximo_mantenimiento).format('DD/MM/YYYY');
                      mantenimientos.push(
                        <Tag key="fecha" color={diasRestantes <= 3 ? 'red' : 'orange'}>
                          Fecha: {fecha} ({diasRestantes} días)
                        </Tag>
                      );
                    }

                    // Mantenimiento por kilometraje
                    if (record.km_proximo_mantenimiento && record.km_actual !== null) {
                      const kmRestantes = parseFloat(record.km_restantes || 0);
                      const kmProximo = parseFloat(record.km_proximo_mantenimiento).toFixed(2);
                      mantenimientos.push(
                        <Tag key="km" color={kmRestantes <= 50 ? 'red' : 'orange'}>
                          Km: {kmProximo} km (faltan {kmRestantes.toFixed(2)} km)
                        </Tag>
                      );
                    }

                    return mantenimientos.length > 0 ? <Space direction="vertical" size="small">{mantenimientos}</Space> : '-';
                  },
                },
                {
                  title: 'Días Restantes',
                  dataIndex: 'dias_restantes',
                  key: 'dias_restantes',
                  render: (dias) => (
                    <Tag color={dias <= 3 ? 'red' : 'orange'}>
                      {dias} día{dias !== 1 ? 's' : ''}
                    </Tag>
                  ),
                },
              ]}
              dataSource={stats.mantenimientos_proximos}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No hay unidades con mantenimiento próximo (menos de 5 días o 100 km).
          </div>
        )}
      </Card>
      <Card title="Servicios Recientes" style={{ marginTop: 24 }}>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={stats?.servicios_recientes || []}
            rowKey="id"
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      <div style={{ marginTop: 24 }}>
        <VencimientosCard />
      </div>

      <div style={{ marginTop: 24 }}>
        <BajoStockCard />
      </div>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span>
                <TrophyOutlined style={{ color: '#faad14', marginRight: 8 }} />
                Ranking de Operarios por Servicios Realizados
              </span>
            }
            headStyle={{ backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}
          >
            {stats?.ranking_servicios && stats.ranking_servicios.length > 0 ? (
              <Table
                columns={[
                  {
                    title: 'Posición',
                    key: 'posicion',
                    width: 80,
                    render: (_, __, index) => {
                      const medalColors = {
                        0: '#ffd700', // Oro
                        1: '#c0c0c0', // Plata
                        2: '#cd7f32', // Bronce
                      };
                      return (
                        <span style={{ fontSize: '18px', fontWeight: 'bold', color: medalColors[index] || '#666' }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {!medalColors[index] && `#${index + 1}`}
                        </span>
                      );
                    },
                  },
                  {
                    title: 'Operario',
                    key: 'operario',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{record.nombre_completo}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {record.documento} - {record.cargo}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Total Servicios',
                    dataIndex: 'total_servicios',
                    key: 'total_servicios',
                    align: 'center',
                    render: (total) => (
                      <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {total}
                      </Tag>
                    ),
                  },
                ]}
                dataSource={stats.ranking_servicios}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No hay datos disponibles
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <span>
                <BarChartOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                Ranking de Operarios por Ingresos Generados
              </span>
            }
            headStyle={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}
          >
            {stats?.ranking_ingresos && stats.ranking_ingresos.length > 0 ? (
              <Table
                columns={[
                  {
                    title: 'Posición',
                    key: 'posicion',
                    width: 80,
                    render: (_, __, index) => {
                      const medalColors = {
                        0: '#ffd700', // Oro
                        1: '#c0c0c0', // Plata
                        2: '#cd7f32', // Bronce
                      };
                      return (
                        <span style={{ fontSize: '18px', fontWeight: 'bold', color: medalColors[index] || '#666' }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {!medalColors[index] && `#${index + 1}`}
                        </span>
                      );
                    },
                  },
                  {
                    title: 'Operario',
                    key: 'operario',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{record.nombre_completo}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {record.documento} - {record.cargo}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Total Ingresos',
                    dataIndex: 'total_ingresos',
                    key: 'total_ingresos',
                    align: 'right',
                    render: (total) => (
                      <span style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '14px' }}>
                        Q {parseFloat(total).toFixed(2)}
                      </span>
                    ),
                  },
                  {
                    title: 'Servicios',
                    dataIndex: 'total_servicios',
                    key: 'total_servicios',
                    align: 'center',
                    width: 100,
                    render: (total) => (
                      <Tag color="default" style={{ fontSize: '12px' }}>
                        {total}
                      </Tag>
                    ),
                  },
                ]}
                dataSource={stats.ranking_ingresos}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No hay datos disponibles
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;

